import type { Field, FamiliarityRecord, Grade, RoundAnswers, SetRecord } from "../types";

export const POINTS_BY_GRADE: Record<Grade, number> = {
  correct: 40,
  close: 20,
  incorrect: 0,
  skip: 0,
};

export const HINTED_CORRECT_POINTS = 5;

export const FAMILIARITY_MULTIPLIER: Record<Grade, number> = {
  correct: 0.18,
  close: 0.08,
  incorrect: 0,
  skip: 0,
};

/** Normalize text for canonical name comparison and ordered suggestions. */
export function normalizeText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/[']+/g, "")
    .replace(/[\p{P}\p{S}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function compactNormalized(value: string): string {
  return normalizeText(value).replace(/\s/g, "");
}

/**
 * Broad one-letter searches can be dominated by a known family of names.
 * Values are normalized prefixes that should be shown after other names
 * beginning with the queried letter.
 */
export const SINGLE_LETTER_DOWNRANKINGS: ReadonlyMap<string, string> = new Map([
  ["p", "pop"],
]);

/**
 * Mask a set name without exposing the length of words after the first one.
 * The first word keeps its first character and length; later words use a
 * fixed mask so the player gets a useful clue without the full shape. Long
 * names show only the first two masked words followed by an ellipsis.
 */
export function maskNameHint(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const firstWord = Array.from(words[0]);
  const firstMask = `${firstWord[0] ?? ""}${"*".repeat(Math.max(0, firstWord.length - 1))}`;
  const remainingMasks = words.slice(1, 2).map(() => "**...");
  return [firstMask, ...remainingMasks].join(" ");
}

function isOrderedSubsequence(query: string, candidate: string): boolean {
  let queryIndex = 0;
  for (const character of candidate) {
    if (character === query[queryIndex]) queryIndex += 1;
    if (queryIndex === query.length) return true;
  }
  return query.length === 0;
}

export interface RankedMatch<T> {
  item: T;
  rank: 0 | 1 | 2 | 3 | 4 | 5;
  normalized: string;
  directTokenMatches: number;
}

function isSingleLetterQuery(query: string): boolean {
  return Array.from(query).length === 1;
}

function isDownrankedSingleLetterMatch(query: string, normalized: string): boolean {
  const downranking = SINGLE_LETTER_DOWNRANKINGS.get(query);
  return downranking ? normalized.startsWith(compactNormalized(downranking)) : false;
}

/**
 * Rank exact, direct-token, prefix, contiguous, then ordered-subsequence
 * matches. The original input order is retained within a rank, after favoring
 * candidates with more exact query-token matches.
 */
export function rankMatches<T>(items: readonly T[], query: string, getText: (item: T) => string): RankedMatch<T>[] {
  const normalizedQuery = normalizeText(query);
  const queryTokens = normalizedQuery.split(" ").filter(Boolean);
  const compactQuery = normalizedQuery.replace(/\s/g, "");
  if (!compactQuery) {
    return items.map((item) => ({ item, rank: 5 as const, normalized: compactNormalized(getText(item)), directTokenMatches: 0 }));
  }
  const singleLetterQuery = isSingleLetterQuery(compactQuery);

  return items
    .map((item, index) => {
      const normalizedValue = normalizeText(getText(item));
      const normalized = normalizedValue.replace(/\s/g, "");
      const candidateTokens = normalizedValue.split(" ").filter(Boolean);
      const directTokenMatches = queryTokens.reduce((count, token) => count + (candidateTokens.includes(token) ? 1 : 0), 0);
      const orderedMatch = isOrderedSubsequence(compactQuery, normalized);
      let rank: 0 | 1 | 2 | 3 | 4 | 5 | null = null;
      if (singleLetterQuery) {
        if (normalized === compactQuery) rank = 0;
        else if (normalized.startsWith(compactQuery) && !isDownrankedSingleLetterMatch(compactQuery, normalized)) rank = 3;
        else if (normalized.startsWith(compactQuery)) rank = 4;
        else if (normalized.includes(compactQuery)) rank = 5;
      } else if (normalized === compactQuery) rank = 0;
      else if (orderedMatch && directTokenMatches === queryTokens.length) rank = 1;
      else if (orderedMatch && directTokenMatches > 0) rank = 2;
      else if (normalized.startsWith(compactQuery)) rank = 3;
      else if (normalized.includes(compactQuery)) rank = 4;
      else if (orderedMatch) rank = 5;
      return rank === null ? null : { item, rank, normalized, directTokenMatches, index };
    })
    .filter((match): match is { item: T; rank: 0 | 1 | 2 | 3 | 4 | 5; normalized: string; directTokenMatches: number; index: number } => match !== null)
    .sort((left, right) => left.rank - right.rank || right.directTokenMatches - left.directTokenMatches || left.index - right.index)
    .map(({ item, rank, normalized, directTokenMatches }) => ({ item, rank, normalized, directTokenMatches }));
}

export function limitMatches<T>(matches: RankedMatch<T>[], limit = 20): { matches: RankedMatch<T>[]; hasMore: boolean } {
  return { matches: matches.slice(0, limit), hasMore: matches.length > limit };
}

export function limitMatchesForQuery<T>(matches: RankedMatch<T>[], query: string, limit = 20): { matches: RankedMatch<T>[]; hasMore: boolean } {
  return isSingleLetterQuery(compactNormalized(query)) ? { matches, hasMore: false } : limitMatches(matches, limit);
}

/** Match a record against several searchable fields without joining fields together. */
export function rankMatchesByFields<T>(items: readonly T[], query: string, getValues: (item: T) => readonly string[]): RankedMatch<T>[] {
  if (!compactNormalized(query)) return items.map((item) => ({ item, rank: 5 as const, normalized: "", directTokenMatches: 0 }));
  return items
    .map((item, index) => {
      const matches = getValues(item).flatMap((value) => rankMatches([value], query, (candidate) => candidate));
      const best = matches.sort((left, right) => left.rank - right.rank || right.directTokenMatches - left.directTokenMatches)[0];
      return best ? { item, rank: best.rank, normalized: best.normalized, directTokenMatches: best.directTokenMatches, index } : null;
    })
    .filter((match): match is { item: T; rank: 0 | 1 | 2 | 3 | 4 | 5; normalized: string; directTokenMatches: number; index: number } => match !== null)
    .sort((left, right) => left.rank - right.rank || right.directTokenMatches - left.directTokenMatches || left.index - right.index)
    .map(({ item, rank, normalized, directTokenMatches }) => ({ item, rank, normalized, directTokenMatches }));
}

export function gradeName(answer: string, canonical: string): Grade {
  return normalizeText(answer) === normalizeText(canonical) ? "correct" : "incorrect";
}

export function gradeEra(answer: string | undefined, canonicalId: string, canonicalName?: string): Grade {
  if (!answer?.trim()) return "incorrect";
  const normalized = answer.trim().toLocaleLowerCase();
  return normalized === canonicalId.toLocaleLowerCase() || (canonicalName ? normalizeText(answer) === normalizeText(canonicalName) : false)
    ? "correct"
    : "incorrect";
}

export function gradeYear(answer: string | number | undefined, canonicalYear: number): Grade {
  if (answer === undefined || String(answer).trim() === "") return "incorrect";
  const parsed = parseWholeYear(answer);
  if (parsed === null) return "incorrect";
  if (parsed === canonicalYear) return "correct";
  if (Math.abs(parsed - canonicalYear) === 1) return "close";
  return "incorrect";
}

function parseWholeYear(answer: string | number): number | null {
  if (typeof answer === "number") return Number.isInteger(answer) ? answer : null;
  const trimmed = answer.trim();
  if (!/^[+-]?\d+$/.test(trimmed)) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isInteger(parsed) ? parsed : null;
}

export function gradeField(field: Field, answer: string | number | undefined, set: SetRecord, eraId?: string | null): Grade {
  if (field === "name") return gradeName(String(answer ?? ""), set.name);
  if (field === "era") return gradeEra(eraId ?? String(answer ?? ""), set.eraId, set.era);
  return gradeYear(answer, set.year);
}

export function scoreResults(result: Partial<Record<Field, Grade>>, reducedFields: readonly Field[] = []): number {
  const reduced = new Set(reducedFields);
  return Object.entries(result).reduce((total, [field, grade]) => {
    if (!grade) return total;
    const points = grade === "correct" && reduced.has(field as Field) ? HINTED_CORRECT_POINTS : POINTS_BY_GRADE[grade];
    return total + points;
  }, 0);
}

export function isPerfect(result: Partial<Record<Field, Grade>>, asked: readonly Field[]): boolean {
  return asked.length > 0 && asked.every((field) => result[field] === "correct");
}

export function updateFamiliarity(current: number, grade: Grade): number {
  const safe = Math.min(1, Math.max(0, Number.isFinite(current) ? current : 0));
  let next: number;
  if (grade === "correct") next = safe + (1 - safe) * 0.18;
  else if (grade === "close") next = safe + (1 - safe) * 0.08;
  else if (grade === "incorrect") next = safe * 0.55;
  else next = safe * 0.9;
  return Math.min(1, Math.max(0, next));
}

export function emptyFamiliarity(): FamiliarityRecord {
  return { name: 0, era: 0, year: 0, seen: 0, lastSeen: null };
}

export function answerIsPresent(field: Field, answers: RoundAnswers, skipped: ReadonlySet<Field>): boolean {
  if (skipped.has(field)) return true;
  const answer = answers[field];
  return answer !== undefined && String(answer).trim().length > 0;
}

export function validationErrors(
  asked: readonly Field[],
  answers: RoundAnswers,
  skipped: ReadonlySet<Field>,
  candidateYears: readonly number[],
): Partial<Record<Field, string>> {
  const errors: Partial<Record<Field, string>> = {};
  for (const field of asked) {
    if (skipped.has(field)) continue;
    if (!answerIsPresent(field, answers, skipped)) {
      errors[field] = "Enter an answer to continue.";
      continue;
    }
    if (field === "year") {
      const value = parseWholeYear(String(answers.year));
      const min = Math.min(...candidateYears);
      const max = Math.max(...candidateYears);
      if (value === null) errors.year = "Use a whole-number year.";
      else if (value < min || value > max) errors.year = `Use a year from ${min} to ${max}.`;
    }
  }
  return errors;
}
